function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function formatMessageTime(dateString: string): string {
  const date = new Date(dateString);
  return formatRelativeDate(date);
}

export function formatRelativeTime(dateInput: Date | string): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return formatRelativeDate(date);
}

export function stripRichTextToPlainText(content: string | null | undefined): string {
  if (!content) return '';

  if (typeof DOMParser !== 'undefined') {
    const document = new DOMParser().parseFromString(content, 'text/html');
    return document.body.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  }

  return content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

const DECISION_TITLE_MAX_LENGTH = 120;
const DECISION_ACTION_KEYWORDS = [
  'should',
  'need',
  'must',
  'focus',
  'priorit',
  'decid',
  'plan',
  'recommend',
  'shift',
  'start',
  'stop',
  'follow up',
  'next step',
];

function trimDecisionTitle(text: string): string {
  const normalized = text.replace(/\s+/g, ' ').trim().replace(/[.!?]+$/, '');
  if (normalized.length <= DECISION_TITLE_MAX_LENGTH) {
    return normalized;
  }

  const truncated = normalized.slice(0, DECISION_TITLE_MAX_LENGTH + 1);
  const lastSpace = truncated.lastIndexOf(' ');
  const safeCutoff = lastSpace > 40 ? lastSpace : DECISION_TITLE_MAX_LENGTH;

  return `${normalized.slice(0, safeCutoff).trim()}...`;
}

function normalizeDecisionCandidate(text: string): string {
  return text
    .replace(/^[\s\-•*\d.)]+/, '')
    .replace(/^(hey|hi|hello)\s+[^,]+,\s*/i, '')
    .replace(/^(just\s+)?wanted\s+to\s+share\s+/i, '')
    .trim();
}

function scoreDecisionCandidate(text: string): number {
  const lowerText = text.toLowerCase();
  let score = 0;

  if (text.length >= 18 && text.length <= DECISION_TITLE_MAX_LENGTH) {
    score += 2;
  }

  if (text.length > DECISION_TITLE_MAX_LENGTH) {
    score -= 1;
  }

  if (/\b(we should|should|need to|must|let's|plan to|recommend|focus|shift|priorit)/i.test(text)) {
    score += 4;
  }

  if (DECISION_ACTION_KEYWORDS.some(keyword => lowerText.includes(keyword))) {
    score += 2;
  }

  if (/\b(wanted to share|quick update|for visibility|fyi|where we stand|checking in)\b/i.test(text)) {
    score -= 4;
  }

  if (/^(hey|hi|hello)\b/i.test(text)) {
    score -= 2;
  }

  return score;
}

export function buildDecisionTitleSuggestion(content: string | null | undefined): string {
  const plainText = stripRichTextToPlainText(content);
  if (!plainText) return '';

  const sentenceMatches = plainText.match(/[^.!?\n]+[.!?]?/g) ?? [plainText];
  const candidates = sentenceMatches
    .map(normalizeDecisionCandidate)
    .filter(Boolean);

  const bestCandidate = candidates
    .map((text, index) => ({ text, score: scoreDecisionCandidate(text), index }))
    .sort((left, right) => right.score - left.score || left.index - right.index)[0]?.text;

  return trimDecisionTitle(bestCandidate ?? normalizeDecisionCandidate(plainText));
}

export function detectUniqueUrls(content: string): number {
  // Keep consistent with MessageList: URL can appear multiple times due to rich text editor
  // (e.g., once as href, once as text), so count unique matches.
  const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;
  const matches = content.match(urlRegex);
  if (!matches) return 0;
  return [...new Set(matches)].length;
}

export function processChannelMentions(content: string, channels: Array<{ id: string; name: string }> | undefined): string {
  if (!channels || channels.length === 0) return content;

  return content.replace(/#([a-z0-9-]+)/gi, (match, channelName) => {
    const channel = channels.find(c => c.name.toLowerCase() === channelName.toLowerCase());
    if (!channel) return match;

    return `<a href="#" class="text-primary hover:underline" data-channel-id="${channel.id}">#${channelName}</a>`;
  });
}
